import React, { useCallback, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { instance } from 'api/axios.instance';
import { localList } from 'app/constants';
import { selectDevice } from 'store/slices/common';
import { Box, Button, Flex, Heading, Separator, Text } from '@radix-ui/themes';
import SocialShare from 'components/common/socialShare';

import './style.scss';

export default function BoardDetail({ boardType }) {
  const deviceType = useSelector(selectDevice);
  const isMobile = deviceType === 'mobile';

  // hooks
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // state
  const data = location.state?.fetchBody;

  const [detailData, setDetailData] = useState({});
  const fetchPostDetail = useCallback(async () => {
    const response = await instance(
      `/post/detail/${id}`,
      data
        ? {
            method: 'POST',
            data: {
              ...data
            }
          }
        : { method: 'GET' }
    );
    setDetailData(response.data.data);
  }, [id, data]);

  useEffect(() => {
    fetchPostDetail();
  }, [fetchPostDetail]);

  const handleDownload = async file => {
    try {
      const userAgent = navigator.userAgent || window.opera;
      const isNaverApp = /NAVER/i.test(userAgent);
      const isMobile = /android|iphone|ipad|ipod/i.test(userAgent);

      // 직접 링크 방식 사용
      if (isNaverApp || (isMobile && !window.chrome)) {
        const link = document.createElement('a');
        link.href = file.fileUrl;
        link.setAttribute('download', file.originalName);
        link.setAttribute('target', '_blank');
        link.setAttribute('rel', 'noopener noreferrer');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        // 기존 blob 방식 사용
        const response = await fetch(file.fileUrl);
        if (!response.ok) throw new Error('Network response was not ok');
        const blob = await response.blob();
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);
        link.setAttribute('download', file.originalName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(link.href);
      }
    } catch (error) {
      console.error('Download error:', error);
      window.open(file.fileUrl, '_blank');
    }
  };

  const goBack = () => {
    navigate(`/${boardType}`);
  };

  // 웹 접근성을 고려한 이미지 대체 텍스트 추가 함수
  const [parsedContent, setParsedContent] = useState('');
  const parseContentWithAlt = (htmlString = '') => {
    if (!htmlString.trim()) return '';

    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlString, 'text/html');
    const postTitle = detailData.post?.title || '';

    doc.querySelectorAll('img').forEach(img => {
      const currentAlt = img.getAttribute('alt')?.trim();
      const isFileName = /\.(jpg|jpeg|png|gif|webp|svg)$/i.test(currentAlt);

      if (!currentAlt || isFileName) {
        img.setAttribute('alt', postTitle);
      }
    });

    doc.querySelectorAll('a[target="_blank"]').forEach(link => {
      const title = link.getAttribute('title');

      if (!title || !title.trim()) {
        link.setAttribute('title', '새 창 열림');
      }
    });

    // QR코드 이미지맵: 포스터 이미지 내 QR코드 영역에 대체텍스트 적용
    // 게시글별 QR코드 위치 설정 (원본 이미지 기준 좌표, 반응형 스케일링은 useEffect에서 처리)
    const QR_MAP_CONFIG = {
      7836: {
        areas: [
          { coords: '116,8309,302,8493', alt: '지원공고&제출서류 확인하기 QR 코드' },
          { coords: '757,8310,941,8495', alt: '카카오톡 문의채널 바로가기 QR 코드' }
        ]
      }
    };

    const qrConfig = QR_MAP_CONFIG[id];
    if (qrConfig) {
      const posterImg = doc.querySelector('img');
      if (posterImg) {
        const mapName = `post-${id}-qr-map`;
        posterImg.setAttribute('usemap', `#${mapName}`);

        const map = doc.createElement('map');
        map.setAttribute('name', mapName);

        qrConfig.areas.forEach(({ coords, alt }) => {
          const area = doc.createElement('area');
          area.setAttribute('shape', 'rect');
          area.setAttribute('data-original-coords', coords);
          area.setAttribute('coords', coords);
          area.setAttribute('alt', alt);
          area.setAttribute('href', '#');
          area.setAttribute('role', 'img');
          area.setAttribute('tabindex', '-1');
          map.appendChild(area);
        });

        doc.body.appendChild(map);
      }
    }

    return doc.body.innerHTML;
  };

  useEffect(() => {
    if (detailData.post?.content) {
      setParsedContent(parseContentWithAlt(detailData.post.content));
    }
  }, [detailData.post?.content]);

  // 이미지맵 반응형 좌표 스케일링: 원본 좌표를 실제 표시 크기에 맞게 재계산
  useEffect(() => {
    const mapName = `post-${id}-qr-map`;
    const img = document.querySelector(`.post-content img[usemap="#${mapName}"]`);
    if (!img) return;

    const scaleImageMap = () => {
      if (!img.naturalWidth || !img.naturalHeight) return;
      const scaleX = img.clientWidth / img.naturalWidth;
      const scaleY = img.clientHeight / img.naturalHeight;

      document.querySelectorAll(`map[name="${mapName}"] area`).forEach(area => {
        const original = area.dataset.originalCoords.split(',').map(Number);
        const scaled = original.map((v, i) => Math.round(i % 2 === 0 ? v * scaleX : v * scaleY));
        area.setAttribute('coords', scaled.join(','));
      });
    };

    if (img.complete && img.naturalWidth) {
      scaleImageMap();
    } else {
      img.addEventListener('load', scaleImageMap);
    }

    window.addEventListener('resize', scaleImageMap);

    return () => {
      img.removeEventListener('load', scaleImageMap);
      window.removeEventListener('resize', scaleImageMap);
    };
  }, [id, parsedContent]);

  // 게시글 등록일 시간 부분 삭제
  const createPostDate = detailData.post?.createDate.slice(0, 10);

  return (
    <>
      <Box className="post-title">
        <Flex direction={isMobile && 'column'} justify="center" align="center" gap={isMobile ? '2' : '6'}>
          <Box className={`chips local ${localList.find(local => local.name === detailData.post?.region)?.en}`}>
            <Text>{detailData.post?.region}</Text>
          </Box>
          <Heading as="h3" className={isMobile ? 'sub-title' : 'title3'}>
            {detailData.post?.title}
          </Heading>
        </Flex>
      </Box>

      <Box className="post-info">
        <Text as="p">등록일 : {createPostDate}</Text>
        <Text as="p">조회수 : {detailData.post?.viewCount}</Text>
      </Box>

      <Box className="post-box">
        <Box className="post-content">
          <div dangerouslySetInnerHTML={{ __html: parsedContent }} />
        </Box>
      </Box>

      <Box className="post-attachment">
        {/* 첨부파일 */}
        {detailData.files?.length > 0 && (
          <Flex direction={isMobile && 'column'}>
            <Box>
              <Text as="p" className={`attachment-title ${isMobile ? 'body2 font-bold' : 'sub-title'} icon-clip`}>
                첨부파일
              </Text>
            </Box>
            <ul className="attachment-list">
              {detailData.files.map((file, index) => (
                <li key={index}>
                  <button
                    type="button"
                    onClick={() => handleDownload(file)}
                    className="file-download-btn"
                    aria-label={`${file.originalName} 다운로드`}
                  >
                    <span className={`icon icon-${file.extension}`} aria-hidden="true" />
                    <Text as="p" className="file">
                      {file.originalName}
                    </Text>
                    <span className="download" aria-hidden="true" />
                  </button>
                </li>
              ))}
            </ul>
          </Flex>
        )}
        {/* 관련링크 */}
        {detailData.post?.link && (
          <Flex direction={isMobile && 'column'}>
            <Box>
              <Text as="p" className={`attachment-title ${isMobile ? 'body2 font-bold' : 'sub-title'} icon-link`}>
                관련링크
              </Text>
            </Box>
            <ul className="attachment-list">
              <li>
                <a
                  href={detailData.post.link?.startsWith('http') ? detailData?.post.link : `https://${detailData.post?.link}`}
                  target="_blank"
                  color="gray"
                  rel="noreferrer"
                  title="새 창 열림"
                >
                  <Text as="p" className="link">
                    {detailData.post.link?.startsWith('http') ? detailData?.post.link : `https://${detailData.post?.link}`}
                  </Text>
                </a>
              </li>
            </ul>
          </Flex>
        )}
      </Box>

      {/* SNS 공유 기능 추가 */}
      <Flex direction={isMobile && 'column'} justify="end" align="center" className="sns-list">
        <SocialShare
          title={detailData.post?.title || '게시글'}
          url={window.location.href}
          description={detailData.post?.content?.replace(/<[^>]*>/g, '').substring(0, 100) + '...' || ''}
          imageUrl={detailData.thumbnails?.[0]?.fileUrl || ''}
        />
        <Button className="register" onClick={goBack}>
          목록
        </Button>
      </Flex>

      <Box className="post-navigation">
        {detailData.post?.prevPost && (
          <Box className="item">
            <Link to={`/${boardType}/detail/${detailData.post?.prevPost?.postNo}`}>
              <Text as="div" className="nav-label">
                이전글
                <Separator orientation={isMobile ? 'horizontal' : 'vertical'} className="divider" decorative />
              </Text>
              <Text as="p">{detailData.post?.prevPost?.title}</Text>
            </Link>
          </Box>
        )}
        {detailData.post?.nextPost && (
          <Box className="item">
            <Link to={`/${boardType}/detail/${detailData.post?.nextPost?.postNo}`}>
              <Text as="div" className="nav-label">
                다음글
                <Separator orientation={isMobile ? 'horizontal' : 'vertical'} className="divider" decorative />
              </Text>
              <Text as="p">{detailData.post?.nextPost?.title}</Text>
            </Link>
          </Box>
        )}
      </Box>
    </>
  );
}
